import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState, useMemo } from 'react';

interface OptimizedUpcomingParams {
  pageSize?: number;
}

export const useOptimizedUpcomingProcurements = ({ pageSize = 25 }: OptimizedUpcomingParams = {}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['upcoming-procurements', currentPage, pageSize],
    queryFn: async () => {
      const now = new Date().toISOString();
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data: procurements, error, count } = await supabase
        .from('procurements')
        .select(`
          *,
          requestor:user_profiles!fk_procurements_requestor(
            id, first_name, last_name, role
          ),
          costs:procurement_costs(*),
          warehouse:procurement_warehouse(*)
        `, { count: 'exact' })
        .gt('expected_delivery_date', now)
        .not('status', 'in', '(rejected,rejected_by_warehouse,rejected_by_finance)')
        .order('expected_delivery_date', { ascending: true })
        .range(from, to);

      if (error) {
        console.error('[OptimizedUpcoming] Error fetching:', error);
        throw error;
      }

      // Batch fetch source names
      const sourceNames = await fetchSourceNamesInBatch(procurements || []);

      // Enrich with source names
      const enriched = (procurements || []).map(p => ({
        ...p,
        source_name: p.source_id ? sourceNames[p.source_id] : null
      }));

      return {
        data: enriched,
        count: count || 0,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  return {
    upcomingProcurements: data?.data || [],
    isLoading,
    error,
    currentPage,
    totalPages: data?.totalPages || 1,
    totalItems: data?.count || 0,
    goToPage,
    hasNextPage: currentPage < (data?.totalPages || 1),
    hasPreviousPage: currentPage > 1,
  };
};

async function fetchSourceNamesInBatch(procurements: any[]): Promise<Record<string, string | null>> {
  const sourceNames: Record<string, string | null> = {};
  
  // Group by source type
  const supplierIds: string[] = [];
  const agentIds: string[] = [];
  const farmIds: string[] = [];

  procurements.forEach(p => {
    if (p.source_id && p.source_type) {
      if (p.source_type === 'supplier') supplierIds.push(p.source_id);
      else if (p.source_type === 'agent') agentIds.push(p.source_id);
      else if (p.source_type === 'farm') farmIds.push(p.source_id);
    }
  });

  // Batch fetch in parallel
  const [suppliers, agents, farms] = await Promise.all([
    supplierIds.length > 0
      ? supabase.from('suppliers').select('id, name').in('id', supplierIds)
      : { data: [] },
    agentIds.length > 0
      ? supabase.from('agents').select('id, name').in('id', agentIds)
      : { data: [] },
    farmIds.length > 0
      ? supabase.from('farms').select('id, farmer_name').in('id', farmIds)
      : { data: [] },
  ]);

  suppliers.data?.forEach(s => { sourceNames[s.id] = s.name; });
  agents.data?.forEach(a => { sourceNames[a.id] = a.name; });
  farms.data?.forEach(f => { sourceNames[f.id] = f.farmer_name; });

  return sourceNames;
}
